// DEPENDENCIES
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";

// CSS
import "./DesignJobChangeLogCard.css";

// MUI COMPONENTS
import Button from "@mui/material/Button";

import Collapse from "@mui/material/Collapse";

import IconButton from "@mui/material/IconButton";

// MUI ICONS
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../../../Misc/J2NoResultsFound/J2NoResultsFound";

// CONSTANTS
import { convertToPST } from "../../../../../constants/dateTime";
import { theme } from "../../../../../constants/theme";
import { getAPICall, postAPICall } from "../../../../../config/apiCalls";
import { useMsal } from "@azure/msal-react";
import TanStackTable from "../../../../Misc/Table/TanStackTable";
import {
	dateSort,
	defaultSort,
	notApplicable,
} from "../../../../Misc/Table/TanStackTableConstants";

import {
	Dialog,
	DialogContent,
	DialogTitle,
	LinearProgress,
	Typography,
} from "@mui/material";

// REACT COMPONENT
const DesignJobChangeLogCard = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [changeLogCollapseOpen, setChangeLogCollapseOpen] = useState(false);
	const [changeLogs, setChangeLogs] = useState([]);

	const [changeLogsColumns, setChangeLogsColumns] = useState([]);
	const [changeLogsRows, setChangeLogsRows] = useState([]);

	const [isDownloading, setIsDownloading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [progressMessage, setProgressMessage] = useState("");

	// USE EFFECT
	useEffect(() => {
		// fetch change logs for the job
		fetchChangeLogs();
	}, [props.designJobRecord]);

	useEffect(() => {
		createColumnData();
		createRowData();
	}, [changeLogs]);

	// creates column data for the change logs table
	const createColumnData = () => {
		setChangeLogsColumns([
			{
				accessorKey: "employee_name",
				header: () => <span>Employee</span>,
				label: "Employee",
				cell: ({ row }) => (
					<p>{row.original.employee_name || notApplicable}</p>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "full_project_name",
				header: () => <span>Job</span>,
				label: "Job",
				cell: ({ row }) =>
					row.original.full_project_name ? (
						<a href={`/design/spec_books/${row.original.phase_id}`}>
							{row.original.full_project_name}
						</a>
					) : (
						notApplicable
					),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "description",
				header: () => <span>Description</span>,
				label: "Description",
				cell: ({ row }) => (
					<p>{row.original.description || notApplicable}</p>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "created",
				header: () => <span>Date</span>,
				label: "Date",
				cell: ({ row }) => (
					<p>{row.original.created || notApplicable}</p>
				),
				filterType: "date",
				filterFn: dateSort,
			},
		]);
	};

	// creates row data for the change logs table
	const createRowData = () => {
		setChangeLogsRows(
			changeLogs.map((log) => ({
				employee_name: log.employee_name,
				full_project_name: log.full_project_name,
				description: log.description,
				created: convertToPST(log.created),
			}))
		);
	};

	// fetches change logs for the job
	const fetchChangeLogs = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				`/api/logs/designJobs/byDesignJob?propertyID=${props.designJobRecord.property_id}&projectID=${props.designJobRecord.project_id}&phaseID=${props.designJobRecord.id}`
			);

			setChangeLogs(res.data);
		} catch (error) {
			console.log("Error in fetchChangeLogs: ", error);
		}
	};

	const downloadButtonClicked = async () => {
		setIsDownloading(true);
		setProgress(0); // this is the percentage
		setProgressMessage("Generating...");

		try {
			// Create Each Spec PDF:
			setProgressMessage("Creating Document...");
			setProgress(50);

			// Format change logs into HTML table format
			const htmlDesignJobRecord = `
				<figure class="table" style="width:100%; margin:0; padding:0; page-break-before: avoid; break-before: avoid;">
				<table class="ck-table-resized">
					<thead>
					<tr>
						<th><span class="bold">Employee</span></th>
						<th><span class="bold">Job</span></th>
						<th><span class="bold">Description</span></th>
						<th><span class="bold">Date</span></th>
					</tr>
					</thead>
					<tbody>
					${changeLogsRows
						.map(
							(row) => `
						<tr>
						<td>${row.employee_name || "N/A"}</td>
						<td>${row.full_project_name || "N/A"}</td>
						<td>${row.description || "N/A"}</td>
						<td>${row.created || "N/A"}</td>
						</tr>
					`
						)
						.join("")}
					</tbody>
				</table>
				</figure>
				`;

			const tempSelectedSpecsRes = await postAPICall(
				instance,
				accounts[0],
				"/api/convert/html-to-pdf",
				{
					htmlContent: htmlDesignJobRecord,
					property: "",
					address: "",
					date: convertToPST(dayjs.utc(new Date()).toISOString()),
					subtitle:
						"Change Log for " + props.designJobRecord.job_name,
					title: "Change Log for " + props.designJobRecord.job_name,
					spec_title: "",
					spec_subtitle: "",
					include_footer: true,
					include_header: true,
				}
			);

			// Automatically open the generated PDF URL in a new tab
			if (tempSelectedSpecsRes.data.Files.length > 0) {
				window.open(tempSelectedSpecsRes.data.Files[0].Url, "_blank");
			}
		} catch (error) {
			console.log("Error in submitButtonClicked: ", error);
		}

		setProgressMessage("Cleaning Up...");
		setProgress(100);
		setIsDownloading(false);
	};

	// RENDER
	return (
		<div className="DesignJobChangeLogCard">
			<div className="DesignJobChangeLogCardHeader">
				<div className="DesignJobChangeLogCardHeaderIconTitleContainer">
					<IconButton sx={{ color: theme.palette.black.main }}>
						<PublishedWithChangesIcon
							sx={{ fontSize: 32, marginLeft: "8px" }}
						/>
					</IconButton>
					<h2 className="DesignJobChangeLogCardHeaderTitle">
						Change Log
					</h2>
				</div>
				<div className="DesignJobChangeLogCardHeaderButtons">
					<Button
						className="DesignJobChangeLogCardHeaderButton"
						onClick={() => downloadButtonClicked()}
						color="primary"
						variant="outlined"
						disabled={!changeLogsRows.length}
					>
						Download
					</Button>
				</div>
			</div>
			<div className="DesignJobChangeLogCardContent">
				<Collapse in={changeLogCollapseOpen}>
					{!props.designJobRecord && <J2NoResultsFound />}
					{props.designJobRecord && (
						<div style={{ paddingTop: "1em" }}>
							<TanStackTable
								columnData={changeLogsColumns}
								rowData={changeLogsRows}
							/>
						</div>
					)}
				</Collapse>
				<div style={{ display: "flex", justifyContent: "center" }}>
					<Button
						onClick={() =>
							setChangeLogCollapseOpen(!changeLogCollapseOpen)
						}
						variant="text"
						color="primary"
					>
						{changeLogCollapseOpen ? "Show Less" : "Show More"}
					</Button>
				</div>
			</div>
			<Dialog
				id="downloadSingleSpecDialog"
				open={isDownloading}
				fullWidth
				disableEscapeKeyDown
			>
				<DialogTitle id="downloadSingleSpecDialogTitle">
					Generating Document
				</DialogTitle>
				<DialogContent id="downloadSingleSpecDialogContent">
					<div className="loadingBarContainer">
						<LinearProgress
							variant="determinate"
							value={progress}
						/>
						<Typography>{progressMessage}</Typography>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
};

// EXPORT
export default DesignJobChangeLogCard;
